import React, { useMemo, useState, useCallback } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit3,
  FiEye,
  FiEyeOff,
  FiFlag,
  FiLayers,
  FiMessageSquare,
  FiPlusCircle,
  FiTag,
  FiTarget,
  FiUserCheck,
} from 'react-icons/fi';
import {
  formatDateTime,
  formatDetailValue,
  formatRelativeTime,
  getActivityDescription,
} from './inquiriesShared';
import './Timeline.css';

const GROUP_WINDOW_MS = 10 * 60 * 1000;

const EVENT_ICONS = {
  created: FiPlusCircle,
  stage_changed: FiFlag,
  assignment_changed: FiUserCheck,
  note_added: FiMessageSquare,
  casework_updated: FiEdit3,
  progression_updated: FiTarget,
  marked_read: FiEye,
  marked_unread: FiEyeOff,
  custom_status_changed: FiTag,
  group: FiLayers,
};

const EVENT_COLORS = {
  created: 'var(--color-success)',
  stage_changed: 'var(--brand-primary)',
  assignment_changed: 'var(--accent-amber)',
  note_added: 'var(--accent-purple)',
  casework_updated: 'var(--accent-cyan)',
  progression_updated: 'var(--brand-primary)',
  marked_read: 'var(--color-success)',
  marked_unread: 'var(--text-tertiary)',
  custom_status_changed: 'var(--color-info)',
  group: 'var(--text-tertiary)',
};

const MAJOR_EVENTS = new Set([
  'created',
  'stage_changed',
  'assignment_changed',
  'note_added',
]);

const isMajorEvent = (eventType) => MAJOR_EVENTS.has(eventType);

const isGroupableType = (eventType) => {
  return eventType === 'casework_updated'
    || eventType === 'progression_updated'
    || eventType === 'marked_read'
    || eventType === 'marked_unread'
    || eventType === 'custom_status_changed';
};

const getActorKey = (entry) => entry?.actor?.display_name || entry?.actor?.email || 'System';

const groupConsecutiveEntries = (entries) => {
  const result = [];
  let i = 0;

  while (i < entries.length) {
    const current = entries[i];
    const group = [current];
    let j = i + 1;

    if (isGroupableType(current.event_type)) {
      while (j < entries.length) {
        const next = entries[j];
        const sameType = next.event_type === current.event_type;
        const sameActor = getActorKey(next) === getActorKey(current);
        const withinWindow = Math.abs(
          new Date(current.created_at || 0).getTime()
          - new Date(next.created_at || 0).getTime(),
        ) <= GROUP_WINDOW_MS;

        if (sameType && sameActor && withinWindow) {
          group.push(next);
          j += 1;
        } else {
          break;
        }
      }
    }

    if (group.length > 1) {
      result.push({
        id: `group-${i}`,
        isGroup: true,
        count: group.length,
        entries: group,
        event_type: current.event_type,
        created_at: group[0].created_at,
        actor: current.actor,
      });
    } else {
      result.push(current);
    }

    i = j;
  }

  return result;
};

const getGroupTitle = (group) => {
  const type = group.event_type;
  if (type === 'casework_updated') return `${group.count} attributtoppdateringer`;
  if (type === 'progression_updated') return `${group.count} kundeløpsoppdateringer`;
  if (type === 'marked_read') return `${group.count} ganger markert som lest`;
  if (type === 'marked_unread') return `${group.count} ganger markert som ulest`;
  if (type === 'custom_status_changed') return `${group.count} statusendringer`;
  return `${group.count} oppdateringer`;
};

const getTimelineAttributeUpdate = (entry) => {
  if (entry?.event_type !== 'casework_updated' || entry.metadata?.type !== 'field_values') {
    return null;
  }

  const changes = Array.isArray(entry.metadata?.changes)
    ? entry.metadata.changes
        .map((change, index) => ({
          key: change?.key || `change-${index}`,
          label: change?.label || change?.key || `Attributt ${index + 1}`,
          from: change?.from,
          to: change?.to,
        }))
        .filter((change) => change.label)
    : [];
  const fields = Array.isArray(entry.metadata?.fields)
    ? entry.metadata.fields.filter(Boolean)
    : [];

  if (changes.length === 0 && fields.length === 0) {
    return null;
  }

  return {
    changes,
    fields,
    hasExactValues: changes.length > 0,
    title: 'Attributter oppdatert',
    singleTitle: (change) => `${change.label} oppdatert`,
    legacyNote: 'Eksakte verdier ble ikke lagret for denne eldre loggen.',
  };
};

const getTimelineProgressionUpdate = (entry) => {
  if (entry?.event_type !== 'progression_updated') {
    return null;
  }

  const changes = Array.isArray(entry.metadata?.changes)
    ? entry.metadata.changes
        .map((change, index) => ({
          key: change?.key || `progression-change-${index}`,
          label: change?.label || change?.key || `Endring ${index + 1}`,
          from: change?.from,
          to: change?.to,
        }))
        .filter((change) => change.label)
    : [];
  const fields = Array.isArray(entry.metadata?.fields)
    ? entry.metadata.fields.filter(Boolean)
    : [];

  if (changes.length === 0 && fields.length === 0) {
    return null;
  }

  const stepLabel = entry.metadata?.step_label || entry.metadata?.step_key || 'Kundeløp';
  const type = entry.metadata?.type || '';
  const title = type === 'log_created'
    ? `Logg lagt til i ${stepLabel}`
    : type === 'log_updated'
      ? `Logg oppdatert i ${stepLabel}`
      : type === 'log_deleted'
        ? `Logg slettet fra ${stepLabel}`
        : `${stepLabel} oppdatert`;

  const singleSuffix = type === 'log_created'
    ? 'lagt til'
    : type === 'log_deleted'
      ? 'fjernet'
      : 'oppdatert';

  return {
    changes,
    fields,
    hasExactValues: changes.length > 0,
    title,
    singleTitle: (change) => `${stepLabel}: ${change.label} ${singleSuffix}`,
    legacyNote: 'Eksakte verdier ble ikke lagret for denne kundeløp-loggen.',
  };
};

const hasTimelineOldValue = (value) => ![null, undefined, ''].includes(value);

const TimelineChangeRow = ({ change, showAttributeLabel }) => {
  const hasOld = hasTimelineOldValue(change.from);

  const valuesMarkup = hasOld ? (
    <span className="timeline-change-values">
      <span className="timeline-change-value is-old">{formatDetailValue(change.from)}</span>
      <span className="timeline-change-arrow" aria-hidden="true">&rarr;</span>
      <span className="timeline-change-value is-new">{formatDetailValue(change.to)}</span>
    </span>
  ) : (
    <span className="timeline-change-values">
      <span className="timeline-change-value is-new">{formatDetailValue(change.to)}</span>
    </span>
  );

  if (showAttributeLabel) {
    return (
      <div className="timeline-change-row has-label">
        <span className="timeline-change-label">{change.label}</span>
        {valuesMarkup}
      </div>
    );
  }

  return (
    <div className="timeline-change-row">
      {valuesMarkup}
    </div>
  );
};

const TimelineChangeGrid = ({ changes, showAttributeLabels, isNewValues }) => {
  if (!Array.isArray(changes) || changes.length === 0) return null;
  return (
    <div className={`timeline-change-grid${showAttributeLabels ? ' has-labels' : ''}`}>
      {changes.map((change, index) => (
        <TimelineChangeRow
          key={`${change.key}-${index}`}
          change={change}
          showAttributeLabel={showAttributeLabels}
        />
      ))}
      {isNewValues ? (
        <p className="timeline-legacy-note">
          Eksakte verdier ble ikke lagret for denne eldre loggen.
        </p>
      ) : null}
    </div>
  );
};

const TimelineLegacyFields = ({ fields, note }) => {
  return (
    <div className="timeline-change-grid">
      {fields.map((field) => (
        <div key={field} className="timeline-change-row">
          <span className="timeline-change-label">{field}</span>
        </div>
      ))}
      {note ? <p className="timeline-legacy-note">{note}</p> : null}
    </div>
  );
};

const TimelineEntry = ({
  entry,
  expanded,
  onToggle,
}) => {
  const isGroup = Boolean(entry.isGroup);
  const eventType = entry.event_type || '';
  const IconComponent = EVENT_ICONS[isGroup ? 'group' : eventType] || FiFlag;
  const iconColor = EVENT_COLORS[isGroup ? 'group' : eventType] || 'var(--text-tertiary)';
  const major = isMajorEvent(isGroup ? entry.entries[0]?.event_type : eventType);
  const actorName = entry.actor?.display_name || entry.actor?.email || 'System';
  const relativeTime = formatRelativeTime(entry.created_at);
  const fullDateTime = formatDateTime(entry.created_at);
  const hasExpandableDetail = hasEntryDetail(entry, isGroup);

  const title = isGroup
    ? getGroupTitle(entry)
    : getEntryTitle(entry);

  return (
    <li className={`timeline-entry${major ? ' is-major' : ''}${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="timeline-entry-trigger"
        onClick={hasExpandableDetail ? onToggle : undefined}
        aria-expanded={hasExpandableDetail ? expanded : undefined}
        disabled={!hasExpandableDetail}
      >
        <span
          className="timeline-entry-icon"
          style={{ color: iconColor }}
          aria-hidden="true"
        >
          <IconComponent size={14} />
        </span>
        <span className="timeline-entry-body">
          <span className="timeline-entry-title">
            <span className="timeline-entry-label">{title}</span>
            <span className="timeline-entry-time">{relativeTime}</span>
          </span>
          <span className="timeline-entry-meta">
            {actorName} &middot; {fullDateTime}
            {isGroup ? <span className="timeline-group-badge">{entry.count}</span> : null}
          </span>
        </span>
        {hasExpandableDetail ? (
          <span className="timeline-entry-chevron" aria-hidden="true">
            {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </span>
        ) : null}
      </button>

      {(major || expanded) ? (
        <div className="timeline-entry-detail">
          {isGroup ? (
            <div className="timeline-group-expanded">
              {entry.entries.map((subEntry) => (
                <div key={subEntry.id} className="timeline-sub-entry">
                  <span className="timeline-sub-entry-title">
                    {getEntryTitle(subEntry)}
                  </span>
                  <span className="timeline-sub-entry-time">
                    {formatRelativeTime(subEntry.created_at)}
                  </span>
                  {renderEntryChanges(subEntry)}
                </div>
              ))}
            </div>
          ) : (
            renderEntryChanges(entry, eventType)
          )}
          {!isGroup && entry.note_text ? (
            <p className="timeline-note">{entry.note_text}</p>
          ) : null}
        </div>
      ) : null}

      {isGroup && expanded ? (
        <div className="timeline-group-expanded-footer">
          {entry.entries.map((subEntry) => {
            if (subEntry.note_text) {
              return (
                <div key={`note-${subEntry.id}`} className="timeline-sub-entry-note">
                  <span className="timeline-sub-entry-title">
                    {formatRelativeTime(subEntry.created_at)} &middot; {getActorKey(subEntry)}
                  </span>
                  <p className="timeline-note">{subEntry.note_text}</p>
                </div>
              );
            }
            return null;
          })}
        </div>
      ) : null}
    </li>
  );
};

const getEntryTitle = (entry) => {
  const attributeUpdate = getTimelineAttributeUpdate(entry);
  const progressionUpdate = getTimelineProgressionUpdate(entry);

  if (attributeUpdate) {
    const singleChange = attributeUpdate.changes.length === 1 ? attributeUpdate.changes[0] : null;
    const singleField = !attributeUpdate.hasExactValues && attributeUpdate.fields.length === 1
      ? attributeUpdate.fields[0]
      : null;

    if (singleChange) return attributeUpdate.singleTitle(singleChange);
    if (singleField) return `${singleField} oppdatert`;
    return attributeUpdate.title;
  }

  if (progressionUpdate) {
    const singleChange = progressionUpdate.changes.length === 1 ? progressionUpdate.changes[0] : null;
    if (singleChange) return progressionUpdate.singleTitle(singleChange);
    return progressionUpdate.title;
  }

  return getActivityDescription(entry);
};

const hasEntryDetail = (entry, isGroup) => {
  if (isGroup) return true;

  const eventType = entry.event_type;
  const attributeUpdate = getTimelineAttributeUpdate(entry);
  const progressionUpdate = getTimelineProgressionUpdate(entry);
  const timelineUpdate = attributeUpdate || progressionUpdate;

  if (timelineUpdate) {
    return true;
  }

  if (eventType === 'note_added') return true;
  if (eventType === 'stage_changed' || eventType === 'assignment_changed' || eventType === 'custom_status_changed') {
    return true;
  }

  return false;
};

const renderEntryChanges = (entry) => {
  const attributeUpdate = getTimelineAttributeUpdate(entry);
  const progressionUpdate = getTimelineProgressionUpdate(entry);
  const timelineUpdate = attributeUpdate || progressionUpdate;

  if (!timelineUpdate) {
    if (entry.event_type === 'stage_changed') {
      const from = entry.metadata?.from;
      const to = entry.metadata?.to;
      return (
        <div className="timeline-change-grid">
          <div className="timeline-change-row">
            <span className="timeline-change-value is-old">
              {typeof from === 'object' ? (from.label || 'ukjent') : String(from || 'ukjent')}
            </span>
            <span className="timeline-change-arrow" aria-hidden="true">&rarr;</span>
            <span className="timeline-change-value is-new">
              {typeof to === 'object' ? (to.label || 'ukjent') : String(to || 'ukjent')}
            </span>
          </div>
        </div>
      );
    }

    if (entry.event_type === 'assignment_changed') {
      const from = entry.metadata?.from;
      const to = entry.metadata?.to;
      return (
        <div className="timeline-change-grid">
          <div className="timeline-change-row">
            <span className="timeline-change-value is-old">
              {from?.display_name || from?.email || 'ingen'}
            </span>
            <span className="timeline-change-arrow" aria-hidden="true">&rarr;</span>
            <span className="timeline-change-value is-new">
              {to?.display_name || to?.email || 'ingen'}
            </span>
          </div>
        </div>
      );
    }

    if (entry.event_type === 'custom_status_changed') {
      const from = entry.metadata?.from;
      const to = entry.metadata?.to;
      return (
        <div className="timeline-change-grid">
          <div className="timeline-change-row">
            <span className="timeline-change-value is-old">
              {from?.label || 'kundeløp'}
            </span>
            <span className="timeline-change-arrow" aria-hidden="true">&rarr;</span>
            <span className="timeline-change-value is-new">
              {to?.label || 'kundeløp'}
            </span>
          </div>
        </div>
      );
    }

    return null;
  }

  const exactChanges = timelineUpdate.changes || [];
  const fallbackFields = timelineUpdate.fields || [];
  const hasExact = timelineUpdate.hasExactValues;

  if (hasExact) {
    return (
      <TimelineChangeGrid
        changes={exactChanges}
        showAttributeLabels={exactChanges.length > 1}
        isNewValues={false}
      />
    );
  }

  if (fallbackFields.length > 0) {
    return (
      <TimelineLegacyFields
        fields={fallbackFields}
        note={timelineUpdate.legacyNote}
      />
    );
  }

  return null;
};

const Timeline = ({
  entries,
  canManageInbox,
  noteText,
  onNoteTextChange,
  noteSaving,
  noteError,
  activePopover,
  onTogglePopover,
  onSubmitNote,
  notePopoverRef,
}) => {
  const [expandedEntries, setExpandedEntries] = useState({});

  const toggleEntry = useCallback((entryId) => {
    setExpandedEntries((prev) => ({
      ...prev,
      [entryId]: !prev[entryId],
    }));
  }, []);

  const groupedEntries = useMemo(() => groupConsecutiveEntries(entries), [entries]);

  return (
    <article className={`card timeline-card${activePopover === 'note' ? ' inquiry-detail-popover-card-active' : ''}`}>
      <header className="card-header timeline-header">
        <div className="timeline-header-row">
          <div>
            <h2>Tidslinje</h2>
            <p className="card-subtitle">{entries.length} registrerte hendelser på saken.</p>
          </div>
          <div className="inquiry-detail-popover-anchor inquiry-detail-note-popover-anchor" ref={notePopoverRef}>
            <button
              type="button"
              className="button button-secondary"
              disabled={!canManageInbox || noteSaving}
              onClick={() => onTogglePopover('note')}
              aria-haspopup="dialog"
              aria-expanded={activePopover === 'note'}
            >
              Legg til notat
            </button>
            {activePopover === 'note' ? (
              <div
                className="inquiry-detail-popover inquiry-detail-note-popover"
                role="dialog"
                aria-label="Legg til notat"
              >
                <form className="inquiries-note-box" onSubmit={onSubmitNote}>
                  <label className="inquiry-detail-control" htmlFor="inquiry-note">
                    <span>Internt notat</span>
                    <textarea
                      id="inquiry-note"
                      className="input inquiries-note-textarea"
                      value={noteText}
                      disabled={!canManageInbox || noteSaving}
                      onChange={onNoteTextChange}
                      placeholder="Legg til intern oppfølging, kontekst eller neste steg"
                    />
                  </label>
                  <div className="inquiries-note-actions">
                    <span className="card-subtitle">
                      {canManageInbox ? 'Notater blir synlige i tidslinjen.' : 'Du har kun lesetilgang.'}
                    </span>
                    <div className="inquiry-detail-note-actions">
                      <button
                        type="button"
                        className="button button-secondary"
                        disabled={noteSaving}
                        onClick={() => onTogglePopover(null)}
                      >
                        Avbryt
                      </button>
                      <button
                        type="submit"
                        className="button button-primary"
                        disabled={!canManageInbox || noteSaving}
                      >
                        {noteSaving ? 'Lagrer…' : 'Lagre notat'}
                      </button>
                    </div>
                  </div>
                  {noteError ? <p className="inquiries-note-error">{noteError}</p> : null}
                </form>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {groupedEntries.length === 0 ? (
        <p className="card-subtitle timeline-empty">Ingen aktivitet er registrert ennå.</p>
      ) : (
        <ol className="timeline-list">
          {groupedEntries.map((entry) => (
            <TimelineEntry
              key={entry.id}
              entry={entry}
              expanded={Boolean(expandedEntries[entry.id])}
              onToggle={() => toggleEntry(entry.id)}
            />
          ))}
        </ol>
      )}
    </article>
  );
};

export { getTimelineAttributeUpdate, getTimelineProgressionUpdate };
export default Timeline;
